import React, { useState, useEffect } from 'react';
import { Line, HorizontalBar, Bar, Doughnut, Pie, Radar, Polar, } from 'react-chartjs-2';
import {
  Page,
  withReload,
} from "components/common";
import { withXeroErrors } from "components";
import { actions } from "react-redux-form";
import { Multiselect } from "multiselect-react-dropdown";
import {
  compose,
  withPropsOnChange,
  withStateHandlers,
  branch,
} from "recompose";
import MainCard from "./MainCard"
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LoadingMsg from 'components/common/LoadingMsg';
import { Col, Row } from 'reactstrap';
import Select from "react-select";

const monthNames = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const SuperadminDash = (ctx) => { 
  const { client, } = ctx

  const [organisationList, setOrganisationList] = useState([])
  const [selectedOrganisationList, setSelectedOrganisationList] = useState([])
  const [dashboardData, setDashboardData] = useState({})
  const [dashboardChartData, setDashboardChartData] = useState({})

  const [xeroDashboardChartData, setXeroDashboardChartData] = useState({})
  const [selectedYear, setSelectedYear] = useState({ label: new Date().getFullYear(), value: new Date().getFullYear() })
  const [selectedMonth, setSelectedMonth] = useState({ label: monthNames[new Date().getMonth()], value: new Date().getMonth() + 1 })
  
  const { t } = useTranslation()
  const history = useHistory()

  const cardStyle =  {
    className: 'my-4 col-6',
  }

  const monthOptions = [
    { label: t("January"), value: 1 },
    { label: t("February"), value: 2 },
    { label: t("March"), value: 3 },
    { label: t("April"), value: 4 },
    { label: t("May"), value: 5 },
    { label: t("June"), value: 6 },
    { label: t("July"), value: 7 },
    { label: t("August"), value: 8 },
    { label: t("September"), value: 9 },
    { label: t("October"), value: 10 },
    { label: t("November"), value: 11 },
    { label: t("December"), value: 12 },
  ]

  const yearOptions = () => {
    const currentYear = new Date().getFullYear()
    let years = []

    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push({ label: i, value: i })
    }

    return years
  }

  const getRandomColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const color = `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)}, ${Math.floor(
        Math.random() * 256
      )}, 0.6)`;
      colors.push(color);
    }
    return colors;
  }

  useEffect(() => {
    let selectedOrgList = organisationList?.slice(0, 5) || []

    if (selectedOrgList.length === 0) {
      return
    }

    setSelectedOrganisationList(selectedOrgList)

    return () => {
      setSelectedOrganisationList([])
    }

  }, [organisationList]);

  useEffect(() => { 
    if (selectedOrganisationList.length === 0) {
      return
    }

    let selectedOrgIds = selectedOrganisationList?.map(org => org.id).join(',') || ''

    let params = {
      org_ids: selectedOrgIds,
      month: selectedMonth.value,
      year: selectedYear.value,
    }

    fetchDashboardData(params)

    fetchXeroDashboardData({ ...params, report_type: 'invoices' })

    fetchXeroDashboardData({ ...params, report_type: 'bills' })

    fetchXeroDashboardData({ ...params, report_type: 'manualjournals' })

    fetchXeroDashboardData({ ...params, report_type: 'creditnotes' })

  }, [selectedOrganisationList, selectedYear, selectedMonth])

  const fetchOrganisationList = () => {
    try {
      client.get('connected_organisations', { params: { active: true } }).then(res => {
        setOrganisationList(res.data)
      })
      
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchOrganisationList()
  }, []);

  const fetchDashboardData = (params = {}) => {
    try {
      client.get(`dashboard/report`, { params }).then(res => {
        setDashboardData(res.data);  
      });
      
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchXeroDashboardData = (params = {}) => {
    try {
      let chartData = {}

      client.get(`xero-dashboard/report`, { params }).then(res => { 
        if (params.report_type === 'invoices') {
          chartData['xero_invoice_chart_data'] = convertTableToChartData(res.data?.invoices, 'name', 'value', 'bar', 'Total invoices')
        } else if (params.report_type === 'bills') {
          chartData['xero_bill_chart_data'] = convertTableToChartData(res.data?.bills, 'name', 'value', 'bar', 'Total bills')
        } else if (params.report_type === 'manualjournals') {
          chartData['xero_manual_journal_chart_data'] = convertTableToChartData(res.data?.manualjournals, 'name', 'value', 'bar', 'Total manual journals')
        } else if (params.report_type === 'creditnotes') {
          chartData['xero_credit_note_chart_data'] = convertTableToChartData(res.data?.creditnotes, 'name', 'value', 'bar', 'Total credit notes')
        }

        setXeroDashboardChartData((prev) => ({ ...prev, ...chartData }));
      });
      
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (!dashboardData) return

    let chartData = {}

    chartData['user_chart_data'] = convertTableToChartData(dashboardData.users, 'name', 'value', 'bar', 'Total users')
    
    chartData['timeoff_chart_data'] = convertTableToChartData(dashboardData.time_off, 'name', 'value', 'pie', 'Total time-off')
    
    chartData['payrun_chart_data'] = convertTableToChartData(dashboardData.pay_run, 'name', 'value', 'doughnut', 'Total pay-run')

    chartData['employee_chart_data'] = convertTableToChartData(dashboardData.employee, 'name', 'value', 'bar', 'Total employee')

    chartData['integration_chart_data'] = convertTableToChartData(dashboardData.integration, 'name', 'value', 'doughnut', 'Total integration')

    chartData['loan_chart_data'] = convertTableToChartData(dashboardData.loan_approval, 'name', 'value', 'pie', 'Total loan')

    chartData['last_login_chart_data'] = convertTableToChartData(dashboardData.last_login, 'name', 'value', 'bar', 'Total organisation')

    chartData['subscription_chart_data'] = convertTableToChartData(dashboardData.subscriptions, 'name', 'value', 'bar', 'Total subscription')

    setDashboardChartData((prev) => ({ ...prev, ...chartData })); 

  }, [dashboardData]);

  const convertTableToChartData = (tableData, labelKey, dataKey, chartType, chartTitle) => {
    if (!tableData || !tableData.length) return {};

    const labels = tableData.map(item => item[labelKey]);
    const data = tableData.map(item => item[dataKey]);

    const commonDatasetProperties = {
      label: t(chartTitle),
      data: chartType === 'bubble' || chartType === 'scatter' ? data.map((balance, index) => ({ x: index + 1, y: balance })) : data,
      backgroundColor: chartType === 'doughnut' ? getRandomColors(tableData.length) : 'rgba(54, 162, 235, 0.7)',
    };

    return {
      labels,
      datasets: [commonDatasetProperties],
    };
  };

  const defaultChartOptions = {
    scales: {
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            stepSize: 1,
          }
        }
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            stepSize: 1,
          }
        }
      ]
    } 
  }

  const handleSelect = (selectedOrganisationList) => {
    setSelectedOrganisationList(selectedOrganisationList);
  };

  const handleRemove = (selectedOrganisationList) => {
    setSelectedOrganisationList(selectedOrganisationList);
  };

  const handleYearChange = (selectedYear) => {
    setSelectedYear(selectedYear)
  }

  const handleMonthChange = (selectedMonth) => {
    setSelectedMonth(selectedMonth)
  }

  const multiSelectStyle = {
    chips: { background: '#72B566' },
  }

  return (
    <Page title="Superadmin Dashboard" showTitle={false}>

      <div className="text-center" style={{ marginBottom: "30px", marginTop: "10px" }} >
        <p style={{ fontSize: "2em" }}><Trans>Welcome to the Dashboard</Trans></p>
      </div>

      <div className='my-4'>
        <Row>
          <Col>
            <Multiselect
              options={organisationList || []} // Options to display in the dropdown
              selectedValues={selectedOrganisationList || []} // Preselected value to persist in dropdown
              onSelect={handleSelect} // Function will trigger on select event
              onRemove={handleRemove} // Function will trigger on remove event
              displayValue="name" // Property name to display in the dropdown options
              closeOnSelect
              avoidHighlightFirstOption
              showCheckbox={true}
              style={multiSelectStyle}
              placeholder={t("Select")}
              emptyRecordMsg={t('No options available')}
              selectionLimit={10}
              hideSelectedList={false}
            />
          </Col>
          <Col md="2">
            <Select
              options={yearOptions() || []}
              style={{backgroundColor:'#F4F7FE'}}
              onChange={handleYearChange}
              value={selectedYear}
              isClearable={false}
              placeholder={t("Select Year")}
            />
          </Col>
          <Col md="2">
            <Select
              options={monthOptions || []}
              style={{backgroundColor:'#F4F7FE'}}
              onChange={handleMonthChange}
              value={selectedMonth}
              isClearable
              placeholder={t("Select Month")}
            />
          </Col>
        </Row>
      </div>

      <div className='my-4' style={{ display: 'flex', flexWrap: 'wrap', }}>
        {dashboardData.subscriptions && dashboardChartData?.subscription_chart_data && (
          <div {...cardStyle}>
            <MainCard title={t("All Organisation Subscription")} history={history}>
              <Bar data={dashboardChartData?.subscription_chart_data || []} options={defaultChartOptions} />
            </MainCard>
          </div>
        )}

          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Users")} history={history}>
                <HorizontalBar data={dashboardChartData?.user_chart_data || []} options={ defaultChartOptions } />
              </MainCard>
            </div>
          )}

          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Time-off")} history={history}>
                <Pie data={dashboardChartData?.timeoff_chart_data || []}  />
              </MainCard>
            </div>
          )} 
          
          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Pay-run")} history={history}>
                <Doughnut data={dashboardChartData?.payrun_chart_data || []}  />
              </MainCard>
            </div>
          )} 

          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Employee")} history={history}>
                <HorizontalBar data={dashboardChartData?.employee_chart_data || []} options={ defaultChartOptions } />
              </MainCard>
            </div>
          )}

          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Integration")} history={history}>
                <Doughnut data={dashboardChartData?.integration_chart_data || []} />
              </MainCard>
            </div>
          )}

          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Loan")} history={history}>
                <Pie data={dashboardChartData?.loan_chart_data || []} />
              </MainCard>
            </div>
          )}

          { dashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Last login")} history={history}>
                <Bar data={dashboardChartData?.last_login_chart_data || []} options={{
                  ...defaultChartOptions, 
                  tooltips: {
                    callbacks: {
                      afterLabel: function (tooltipItem, data) {
                        if (dashboardData?.last_login[tooltipItem['index']]) {
                          let organisations = dashboardData?.last_login[tooltipItem['index']].organisations
                          return `\n${organisations.join('\n')}`
                        }

                        return ''
                      }
                    }
                  },
                }} />
              </MainCard>
            </div>
          )}

          { xeroDashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Invoices")} history={history}>
                <HorizontalBar data={xeroDashboardChartData?.xero_invoice_chart_data || []} options={ defaultChartOptions } />
              </MainCard>
            </div>
          )}

          { xeroDashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Bills")} history={history}>
                <HorizontalBar data={xeroDashboardChartData?.xero_bill_chart_data || []} options={ defaultChartOptions } />
              </MainCard>
            </div>
          )}

          { xeroDashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Manual Journals")} history={history}>
                <HorizontalBar data={xeroDashboardChartData?.xero_manual_journal_chart_data || []} options={ defaultChartOptions } />
              </MainCard>
            </div>
          )}

          { xeroDashboardChartData && (
            <div {...cardStyle}>
              <MainCard title={t("Organisation Credit Notes")} history={history}>
                <HorizontalBar data={xeroDashboardChartData?.xero_credit_note_chart_data || []} options={ defaultChartOptions } />
              </MainCard>
            </div>
          )}
        </div>
    </Page>
  );
};

export const enhance = compose(
  withReload(),
  withStateHandlers(
    {},
    {
      setState: () => (state, props) => ({
        ...state,
        ...props,
      }),
    }
  ),
  withPropsOnChange([], ({ client, setState }) => { 
    Promise.props({
      ready: true,
    }).then(setState)
  }),
  branch(
    (props) => !props.ready,
    () => () => <Page><LoadingMsg /></Page> 
  ),
);

export default enhance(SuperadminDash);
